import axios from "axios";
import { createStore } from "zustand/vanilla";
import { useEffect, useState } from "react";
import { getCookie } from "../../../../../utils/cookieUtils";
import { errorNoti } from "../../../../../base/BasicNotifications";
import {
  ACCESS_TOKEN_COOKIE_KEY,
  ADMIN_ACCESS_TOKEN_COOKIE_KEY,
  ADMINS_COACH_ID_COOKIE_KEY,
} from "../../../../../data/enums/TokenEnums";
import {
  executionBlueprintUrls,
  REVIEW_STATUS,
  COMMENT_SCOPE,
  BLUEPRINT_STATE,
  LIVE_REVIEW_GATE,
} from "../../_redux/ExecutionBlueprintUrls";

// The dashboard's own word for the same fact the blueprint payload calls
// `LIVE_REVIEW_IN_PROGRESS`. Duplicated as a local const rather than imported
// from `ExecutionBlueprintStore`, which keeps it private — importing it would
// make the review store depend on the dashboard store for a single string, and
// the gate below takes the stage as an argument precisely to avoid that.
const DASHBOARD_LIVE_REVIEW_STAGE = "LIVE_REVIEW_STARTED";

const nodeInstance = axios.create({
  baseURL: process.env.REACT_APP_NEST_BASE_URL,
});

// ─────────────────────────────────────────────────────────────────────────────
// GET :uniqueId/review's exact payload is unconfirmed against the live
// backend - docs/execution-blueprint/COACH-API.md §5 #1, the handoff doc
// only says "sections/items/comments/counts". Everything below assumes a
// shape that mirrors Strategy Canvas's own GET .../review as closely as the
// documented field names allow (same accepted/to_discuss vocabulary, same
// item/section/overall comment scopes - COACH-API.md §3-4), normalized
// through the functions in this section so that ONLY this section needs
// editing once the real shape is confirmed - nothing downstream (the store's
// own actions, or the screen components) should need to change.
// ─────────────────────────────────────────────────────────────────────────────

// The backend's comment row is documented (08-fe-handoff.md §2) as
// scope_type/scope_key/comment_text/created_by/created_at - notably NO id
// field name is given, even though DELETE :commentId clearly needs one.
// Normalized to `coach_comment_id` (Canvas's own field name) specifically so
// the reused CommentList/CommentComposer components
// (StrategyCanvasHelpers.js) work without modification - tries every
// plausible id field before giving up.
const normalizeComment = (raw) => ({
  ...raw,
  coach_comment_id:
    raw.coach_comment_id ??
    raw.execution_blueprint_comment_id ??
    raw.comment_id ??
    raw.id ??
    null,
});

// Item-level review status/comments field names are unconfirmed - tried in
// order of plausibility. `status` on the raw item is the CEO-authoring
// status (EMPTY..FLAGGED, COACH-API.md §3) and must never be confused with
// the coach's own accepted/to_discuss decision, which is why this reads a
// differently-named field rather than overloading `status`.
const normalizeItem = (raw) => ({
  ...raw,
  review_status: raw.review_status ?? raw.coach_review?.status ?? null,
  reviewed_at: raw.reviewed_at ?? raw.coach_review?.discussed_at ?? null,
  comments: (raw.comments || []).map(normalizeComment),
});

const normalizeSection = (raw) => ({
  ...raw,
  items: (raw.items || []).map(normalizeItem),
  // Canvas calls this `section_comments`; EXW's field name is unconfirmed,
  // so both spellings are tried.
  comments: (raw.section_comments || raw.comments || []).map(
    normalizeComment,
  ),
});

const normalizeReview = (raw) => {
  if (!raw) return null;
  return {
    ...raw,
    sections: (raw.sections || []).map(normalizeSection),
    overall_comments: (raw.overall_comments || []).map(normalizeComment),
    // Resolved explicitly rather than left to the `...raw` spread, because
    // this one field gates whether the whole review screen is editable
    // (ExecutionBlueprintReviewMode's `isSubmitted`). The enum itself is
    // documented - 08-fe-handoff.md §1 - but whether THIS payload carries it
    // at the top level isn't. If it arrives nested and this only relied on
    // the spread, the gate would read `undefined` forever and a submitted
    // review would silently become editable again on reload: accept /
    // to-discuss and comment delete all back, on a review already sent to the
    // CEO. Failing loudly isn't an option here, so it reads the plausible
    // nestings too.
    blueprint_state:
      raw.blueprint_state ??
      raw.blueprint?.blueprint_state ??
      raw.execution_blueprint?.blueprint_state ??
      null,
  };
};

// GET :uniqueId/blueprint-mode is documented in one line only - "read-only
// view, items grouped by section, no review affordances" (COACH-API.md §1 #7,
// §5 #2) with no payload shape given at all. The CEO-side GET :uniqueId IS
// documented verbatim (08-fe-handoff.md §2/:98) as
// `sections: [{ section_key, label, sequence, items: [...] }]`, and since
// blueprint-mode is the same underlying data read through a coach guard, that
// is the shape assumed here.
//
// Two things this deliberately does NOT do:
//   - It doesn't reuse normalizeReview. That one invents `review_status` /
//     `comments` on every item, which are review-mode concepts; blueprint-mode
//     has "no review affordances" by definition, so adding them would put
//     fields in the tree that the grid must then be careful to ignore.
//   - It doesn't sort. Sections arrive "ordered by `sequence`" per the
//     handoff, and items carry `item_order`. Sorting defensively here would
//     silently mask a backend ordering bug instead of surfacing it.
const normalizeBlueprintSection = (raw) => ({
  ...raw,
  // `label` is the backend's own human name for the section (e.g. "Financial
  // WIG") - never derive the display name from `section_key` here. The 4 keys
  // are a fixed enum, but their prettified spelling is the backend's call.
  label: raw.label || "",
  items: (raw.items || []).map((item) => ({ ...item })),
});

const normalizeBlueprint = (raw) => {
  if (!raw) return null;
  return {
    ...raw,
    sections: (raw.sections || []).map(normalizeBlueprintSection),
    // The deck's Execution Blueprint layout puts a BHAG banner above the WIG
    // columns, but BHAG appears NOWHERE in the coach-side contract - not in
    // §2's Blueprint shape, not in §4's coach surface (COACH-API.md's only
    // `bhag` mention is a Strategy Canvas block_key). So it's read from
    // whichever plausible field might carry it and the banner is rendered
    // only if something turns up. Left in rather than dropped because the
    // deck is explicit that BHAG heads this layout - if the backend does
    // start sending it, the grid picks it up with no further change.
    bhag: raw.bhag ?? raw.bhag_text ?? raw.big_hairy_audacious_goal ?? null,
  };
};

// Mutation endpoints are unconfirmed to return fresh aggregate counts
// (COACH-API.md §5 #3) - recomputed client-side exactly like Canvas's own
// recomputeCounts, so the UI is correct even if the backend never sends
// counts back on a PUT/POST.
const recomputeCounts = (review) => {
  let acceptedCount = 0;
  let toDiscussCount = 0;
  let commentCount = (review.overall_comments || []).length;
  let totalItems = 0;

  (review.sections || []).forEach((section) => {
    commentCount += (section.comments || []).length;
    (section.items || []).forEach((item) => {
      totalItems += 1;
      if (item.review_status === REVIEW_STATUS.ACCEPTED) acceptedCount += 1;
      else if (item.review_status === REVIEW_STATUS.TO_DISCUSS)
        toDiscussCount += 1;
      commentCount += (item.comments || []).length;
    });
  });

  return {
    accepted_count: acceptedCount,
    to_discuss_count: toDiscussCount,
    reviewed_count: acceptedCount + toDiscussCount,
    comment_count: commentCount,
    total_items: totalItems,
  };
};

export const useExecutionBlueprintReviewStore = createStore((set, get) => ({
  reviewByUniqueId: {},
  reviewLoading: false,
  submittingReview: false,
  blueprintByUniqueId: {},
  blueprintLoading: false,
  // Set by a successful `startLiveReview` in THIS session:
  // { [uniqueId]: { started_at, step, blueprint_state } }. Not a cache of a
  // fetch — it's the POST's own response, which is the freshest fact available
  // and therefore outranks a review payload that was loaded before the start.
  // Its presence alone means "started"; the contents are for display.
  liveReviewByUniqueId: {},
  startingLiveReview: false,
  // Set by submitReview. Only covers submissions made in this session - a
  // reload loses it, at which point the dashboard's `stage` is the source of
  // truth for whether the review already went in.
  reviewSubmittedByUniqueId: {},

  _resolveAuthHeader: () => {
    const accessToken = getCookie(ACCESS_TOKEN_COOKIE_KEY);
    const adminAccessToken = getCookie(ADMIN_ACCESS_TOKEN_COOKIE_KEY);
    const adminsCoachId = getCookie(ADMINS_COACH_ID_COOKIE_KEY);
    if (accessToken) return `Bearer ${accessToken}`;
    if (adminAccessToken && adminsCoachId) return `Bearer ${adminAccessToken}`;
    return null;
  },

  _headers: () => {
    const authHeader = get()._resolveAuthHeader();
    return authHeader ? { Authorization: authHeader } : {};
  },

  // The single call for the whole Review Mode screen (COACH-API.md §1 #3).
  fetchReview: async (uniqueId, { force = false } = {}) => {
    if (!uniqueId || (get().reviewByUniqueId[uniqueId] && !force)) return;
    set({ reviewLoading: true });
    try {
      const response = await nodeInstance.get(
        executionBlueprintUrls.GET_REVIEW.replace("{uniqueId}", uniqueId),
        { headers: get()._headers() },
      );
      set((state) => ({
        reviewByUniqueId: {
          ...state.reviewByUniqueId,
          [uniqueId]: normalizeReview(response.data?.data),
        },
        reviewLoading: false,
      }));
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't load the review");
      set((state) => ({
        reviewByUniqueId: { ...state.reviewByUniqueId, [uniqueId]: null },
        reviewLoading: false,
      }));
    }
  },

  // The read-only Blueprint Mode view (COACH-API.md §1 #7). Cached by
  // uniqueId like every other resource in this codebase - coaches flip
  // between Review Mode and Blueprint Mode constantly while working through
  // a blueprint, and this is what keeps that toggle instant instead of
  // re-fetching on every switch.
  fetchBlueprintMode: async (uniqueId, { force = false } = {}) => {
    if (!uniqueId || (get().blueprintByUniqueId[uniqueId] && !force)) return;
    set({ blueprintLoading: true });
    try {
      const response = await nodeInstance.get(
        executionBlueprintUrls.GET_BLUEPRINT_MODE.replace(
          "{uniqueId}",
          uniqueId,
        ),
        { headers: get()._headers() },
      );
      set((state) => ({
        blueprintByUniqueId: {
          ...state.blueprintByUniqueId,
          [uniqueId]: normalizeBlueprint(response.data?.data),
        },
        blueprintLoading: false,
      }));
    } catch (error) {
      errorNoti(
        error.response?.data?.message || "Couldn't load the blueprint",
      );
      set((state) => ({
        blueprintByUniqueId: {
          ...state.blueprintByUniqueId,
          [uniqueId]: null,
        },
        blueprintLoading: false,
      }));
    }
  },

  // ───────────────────────────────────────────────────────────────────────
  // Live Review start gate — SERVER-BACKED as of 2026-08-27.
  //
  // This used to be a localStorage stand-in (`ebLiveReviewStarted:{uniqueId}`),
  // because at the time the handoff said outright that live review had "no
  // sub-states designed, no endpoints". `12-live-review-fe-handoff.md` §4
  // shipped the endpoint, so the stand-in is gone: the key is no longer read or
  // written anywhere, and the flag now comes from the blueprint's own
  // `blueprint_state`.
  //
  // Everything the old comment warned about is fixed by that. The gate now
  // survives a reload, a second device and an incognito window, and the backend
  // knows the session happened — which it has to, because the CEO's app reads
  // the same fact from its own `GET :uniqueId` in order to show a Join button.
  // A per-browser flag could never have supported a second participant.
  //
  // ⚠️ Any leftover `ebLiveReviewStarted:*` keys in a coach's browser are now
  // inert. They are deliberately NOT cleaned up: a coach with a stale key from
  // a test session would otherwise have it silently deleted mid-session, and
  // the key has no effect either way.
  startLiveReview: async (uniqueId) => {
    if (!uniqueId) return { ok: false, code: "missing_unique_id" };
    set({ startingLiveReview: true });
    try {
      const response = await nodeInstance.post(
        executionBlueprintUrls.START_LIVE_REVIEW.replace(
          "{uniqueId}",
          uniqueId,
        ),
        undefined,
        { headers: get()._headers() },
      );
      const data = response.data?.data || null;
      set((state) => ({
        startingLiveReview: false,
        liveReviewByUniqueId: {
          ...state.liveReviewByUniqueId,
          [uniqueId]: {
            started_at: data?.live_review_started_at ?? null,
            step: data?.live_review_step ?? null,
            blueprint_state:
              data?.blueprint_state ?? BLUEPRINT_STATE.LIVE_REVIEW_IN_PROGRESS,
          },
        },
      }));
      return { ok: true, data };
    } catch (error) {
      const status = error.response?.status;
      set({ startingLiveReview: false });

      // A 409 means the state isn't `COACH_REVIEW_SUBMITTED` (§4). In practice
      // the overwhelmingly likely cause is that live review is ALREADY running
      // — a double-click, a second tab, or a coach returning to a session they
      // started earlier — and from the coach's point of view that isn't a
      // failure, it's the thing they asked for having already happened. So it
      // resolves rather than throwing, the same call the CEO app's
      // `enterCritique` makes on its own 409.
      //
      // The gate below is what protects against the other reading: it resolves
      // from the blueprint's real state, so if the 409 was in fact "too early",
      // the screen bounces back instead of opening on a session that isn't live.
      if (status === 409) {
        return { ok: true, alreadyStarted: true };
      }

      errorNoti(
        error.response?.data?.message || "Couldn't start the live review",
      );
      return { ok: false, code: "request_failed", status };
    }
  },

  // Tri-state, deliberately — see LIVE_REVIEW_GATE for why a boolean is wrong
  // here. Resolution order is most-authoritative-first:
  //
  //   1. A start we performed in this session. The POST's own response is the
  //      freshest fact available and beats a review payload fetched before it.
  //   2. The review payload's `blueprint_state`. This is what makes the gate
  //      survive a reload.
  //   3. The dashboard's `stage`, passed in by the caller rather than read from
  //      the other store — a different vocabulary for the same fact
  //      (`LIVE_REVIEW_STARTED` vs `LIVE_REVIEW_IN_PROGRESS`), and useful
  //      because the dashboard is often loaded when the review isn't.
  //   4. Nothing loaded → UNKNOWN. The screen waits.
  //
  // Synchronous, so the screen can call it during an effect exactly as it called
  // the old localStorage version.
  resolveLiveReviewGate: (uniqueId, { dashboardStage } = {}) => {
    if (!uniqueId) return LIVE_REVIEW_GATE.NOT_STARTED;

    if (get().liveReviewByUniqueId[uniqueId]) return LIVE_REVIEW_GATE.STARTED;

    const review = get().reviewByUniqueId[uniqueId];
    const state = review?.blueprint_state;
    if (state === BLUEPRINT_STATE.LIVE_REVIEW_IN_PROGRESS) {
      return LIVE_REVIEW_GATE.STARTED;
    }
    // A known state that ISN'T live review is a real "no", not a "don't know".
    // This is the branch that makes the gate actually gate.
    if (state) return LIVE_REVIEW_GATE.NOT_STARTED;

    if (dashboardStage === DASHBOARD_LIVE_REVIEW_STAGE) {
      return LIVE_REVIEW_GATE.STARTED;
    }
    if (dashboardStage) return LIVE_REVIEW_GATE.NOT_STARTED;

    return LIVE_REVIEW_GATE.UNKNOWN;
  },

  // Accept / To-discuss on a single item (COACH-API.md §1 #4). Overwrites
  // any previous decision - there's no "unset back to pending" endpoint.
  // Applied optimistically, mirroring Strategy Canvas's setBlockStatus
  // exactly: flip it here before the PUT resolves, reconcile with whatever
  // the server actually saved, and on failure roll back to the previous
  // decision rather than clearing it.
  setItemStatus: async (uniqueId, itemId, status) => {
    const review = get().reviewByUniqueId[uniqueId];
    if (!review) return false;

    const applyStatus = (targetReview, nextStatus, nextReviewedAt) => {
      const updated = {
        ...targetReview,
        sections: targetReview.sections.map((section) => ({
          ...section,
          items: section.items.map((item) =>
            item.execution_blueprint_item_id === itemId
              ? { ...item, review_status: nextStatus, reviewed_at: nextReviewedAt }
              : item,
          ),
        })),
      };
      return { ...updated, ...recomputeCounts(updated) };
    };

    const previousItem = review.sections
      .flatMap((section) => section.items)
      .find((item) => item.execution_blueprint_item_id === itemId);
    const previousStatus = previousItem?.review_status ?? null;
    const previousReviewedAt = previousItem?.reviewed_at ?? null;

    set((state) => ({
      reviewByUniqueId: {
        ...state.reviewByUniqueId,
        [uniqueId]: applyStatus(
          state.reviewByUniqueId[uniqueId] || review,
          status,
          new Date().toISOString(),
        ),
      },
    }));

    try {
      const response = await nodeInstance.put(
        executionBlueprintUrls.SET_ITEM_STATUS.replace(
          "{uniqueId}",
          uniqueId,
        ).replace("{itemId}", itemId),
        { status },
        { headers: get()._headers() },
      );
      const saved = response.data?.data || { status, reviewed_at: null };
      set((state) => {
        const current = state.reviewByUniqueId[uniqueId];
        if (!current) return state;
        return {
          reviewByUniqueId: {
            ...state.reviewByUniqueId,
            [uniqueId]: applyStatus(current, saved.status, saved.reviewed_at),
          },
        };
      });
      return true;
    } catch (error) {
      errorNoti(
        error.response?.data?.message || "Couldn't save the review decision",
      );
      set((state) => {
        const current = state.reviewByUniqueId[uniqueId];
        if (!current) return state;
        return {
          reviewByUniqueId: {
            ...state.reviewByUniqueId,
            [uniqueId]: applyStatus(
              current,
              previousStatus,
              previousReviewedAt,
            ),
          },
        };
      });
      return false;
    }
  },

  // Add an item / section / overall comment (COACH-API.md §1 #5). Newest
  // comment kept at the front of its array, matching Canvas's own
  // comments[0]-is-latest convention.
  addComment: async (uniqueId, { scopeType, scopeKey, commentText }) => {
    try {
      const response = await nodeInstance.post(
        executionBlueprintUrls.ADD_COMMENT.replace("{uniqueId}", uniqueId),
        {
          scope_type: scopeType,
          scope_key: scopeKey ?? null,
          comment_text: commentText,
        },
        { headers: get()._headers() },
      );
      if (!response.data?.data) return null;
      const comment = normalizeComment(response.data.data);

      set((state) => {
        const review = state.reviewByUniqueId[uniqueId];
        if (!review) return state;

        let updated = review;
        if (scopeType === COMMENT_SCOPE.ITEM) {
          updated = {
            ...review,
            sections: review.sections.map((section) => ({
              ...section,
              items: section.items.map((item) =>
                item.execution_blueprint_item_id === scopeKey
                  ? { ...item, comments: [comment, ...(item.comments || [])] }
                  : item,
              ),
            })),
          };
        } else if (scopeType === COMMENT_SCOPE.SECTION) {
          updated = {
            ...review,
            sections: review.sections.map((section) =>
              section.section_key === scopeKey
                ? {
                    ...section,
                    comments: [comment, ...(section.comments || [])],
                  }
                : section,
            ),
          };
        } else {
          updated = {
            ...review,
            overall_comments: [comment, ...(review.overall_comments || [])],
          };
        }

        return {
          reviewByUniqueId: {
            ...state.reviewByUniqueId,
            [uniqueId]: { ...updated, ...recomputeCounts(updated) },
          },
        };
      });
      return comment;
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't save the comment");
      return null;
    }
  },

  // Delete one of the coach's own comments (COACH-API.md §1 #6). Endpoint
  // only takes the comment id, so on success this scans item/section/overall
  // comment arrays and drops whichever one matches - same approach as
  // Canvas's deleteComment.
  deleteComment: async (uniqueId, commentId) => {
    try {
      await nodeInstance.delete(
        executionBlueprintUrls.DELETE_COMMENT.replace(
          "{uniqueId}",
          uniqueId,
        ).replace("{commentId}", commentId),
        { headers: get()._headers() },
      );
      set((state) => {
        const review = state.reviewByUniqueId[uniqueId];
        if (!review) return state;
        const dropById = (comments = []) =>
          comments.filter(
            (comment) => comment.coach_comment_id !== commentId,
          );
        const updated = {
          ...review,
          overall_comments: dropById(review.overall_comments),
          sections: review.sections.map((section) => ({
            ...section,
            comments: dropById(section.comments),
            items: section.items.map((item) => ({
              ...item,
              comments: dropById(item.comments),
            })),
          })),
        };
        return {
          reviewByUniqueId: {
            ...state.reviewByUniqueId,
            [uniqueId]: { ...updated, ...recomputeCounts(updated) },
          },
        };
      });
      return true;
    } catch (error) {
      errorNoti(
        error.response?.data?.message || "Couldn't delete the comment",
      );
      return false;
    }
  },

  // "Complete review & send to CEO" (COACH-API.md §1 #8). Blocked server-
  // side until every item has a review status; fires the CEO notification
  // email.
  submitReview: async (uniqueId, body = {}) => {
    if (!uniqueId || get().submittingReview) return null;
    set({ submittingReview: true });
    try {
      const response = await nodeInstance.post(
        executionBlueprintUrls.SUBMIT_REVIEW.replace("{uniqueId}", uniqueId),
        body,
        { headers: get()._headers() },
      );
      const data = response.data?.data || {};
      // Remember that this blueprint's review went in, and with what meeting
      // (if any), so the review screen can offer Live Review straight away
      // instead of needing a dashboard round-trip to learn its stage moved to
      // REVIEW_SUBMITTED. `body` is the fallback because the submit response
      // shape is unconfirmed (COACH-API.md §5) - the coach's own input is the
      // one thing we know for certain was sent.
      set((state) => ({
        submittingReview: false,
        reviewSubmittedByUniqueId: {
          ...state.reviewSubmittedByUniqueId,
          [uniqueId]: {
            submitted: true,
            scheduled_date: data.scheduled_date ?? body.scheduled_date ?? null,
            scheduled_time: data.scheduled_time ?? body.scheduled_time ?? null,
            duration_minutes:
              data.duration_minutes ?? body.duration_minutes ?? null,
          },
        },
      }));
      return data;
    } catch (error) {
      errorNoti(
        error.response?.data?.message || "Couldn't send the review to the CEO",
      );
      set({ submittingReview: false });
      return null;
    }
  },
}));

export default useExecutionBlueprintReviewStore;

// React hook wrapper to subscribe to the vanilla store and trigger
// re-renders - same pattern as every other store in this repo.
export const useExecutionBlueprintReviewStoreSelector = (selector) => {
  const [selected, setSelected] = useState(() =>
    selector(useExecutionBlueprintReviewStore.getState()),
  );
  useEffect(() => {
    const unsub = useExecutionBlueprintReviewStore.subscribe((state) => {
      setSelected(() => selector(state));
    });
    return () => unsub();
  }, [selector]);
  return selected;
};
